import { List } from 'immutable';
import React, { Component, Fragment } from 'react';
import { MarketSupply, Product } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import CurrencySelect from '~/CurrencySelect';
import { formatHumanDate, formatHumanDateTime } from '~/DateTime';
import FormattedProductPrice from '~/FormattedProductPrice';
import FilterParameter from '~/utils/FilterParameter/FilterParameter';
import { PricedProduct, ProductPrice } from '~/Product';
import Dataset from '~/ProductDetailModal/Dataset/DatasetContainer';
import ProductGroupAddForm from '~/ProductGroupAddForm';
import ProductHistoryTable from '~/ProductHistoryTable';
import ProductPriceChart from '~/ProductPriceChart';
import ProductPriceInfo from '~/ProductPriceInfo';
import TreeBrowser from '~/TreeBrowser';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Button from '~/UI/Button';
import linkButtonStyles from '~/UI/Button/link.less';
import secondarySmallButtonStyles from '~/UI/Button/secondary-small.less';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Heading from '~/UI/Heading/Heading';
import Icon from '~/UI/Icon/Icon';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';
import ModalBody from '~/UI/Modal/Body/ModalBody';
import ModalContent from '~/UI/Modal/Content/ModalContent';
import ModalFooter from '~/UI/Modal/Footer/ModalFooter';
import ModalHeader from '~/UI/Modal/Header/ModalHeader';
import Modal from '~/UI/Modal/Modal';
import ShareLinkPopover from '~/UI/ShareLinkPopover/ShareLinkPopover';
import Tab from '~/UI/TabBar/Tab/Tab';
import TabBar from '~/UI/TabBar/TabBar';
import tableStyles from '~/UI/Table/default.less';
import Table from '~/UI/Table/Table';
import Tooltip from '~/UI/Tooltip';

import priceInfoStyles from './priceInfo.less';
import Status from './Status/Status';
import styles from './styles.less';

interface Props {
	product: Product | null;
	products: PricedProduct<unknown>[];
	selectedTabName: string;
	prices: List<ProductPrice>;
	currency: string | null;
	shareLink?: string | null;
	filterParameters: FilterParameter<string | string[]>[] | null;
	status?: React.ReactNode;
	emptyValue?: React.ReactNode;
	emptyPrice?: React.ReactNode;
	isFetching: boolean;
	isFetchingPrices?: boolean;
	onFetch(): void;
	onClose(): void;
	onChange(productId: string): void;
	onAddProductToGroup(
		group: UserProductGroup,
		product: Product,
		parameters: FilterParameter<string | string[]>[] | null
	): void;
	onSelectTab(tab: string): void;
	onShare(): void;
	onCurrencyChange(currency: string | null): void;
}

interface State {
	showSharePopover: boolean;
}

export default class DetailModal extends Component<Props, State> {
	static defaultProps = {
		selectedTabName: 'info',
		shareLink: null,
		emptyValue: <span className={styles.notAvailable}>N/A in the dataset</span>,
		emptyPrice: <span className={styles.notAvailable}>N/A</span>,
		filterParameters: null,
		isFetchingPrices: false,
	};

	state = {
		showSharePopover: false,
	};

	sharePopoverContainerElement: HTMLSpanElement | null = null;

	componentDidMount() {
		this.props.onFetch();
	}

	componentDidUpdate(prevProps: Props) {
		const { currency, onFetch } = this.props;
		if (prevProps.currency !== currency) {
			onFetch();
		}
	}

	renderBody(product: Product) {
		const { selectedTabName } = this.props;

		switch (selectedTabName) {
			case 'info':
				return this.renderInfo(product);
			case 'source':
				return this.renderSource(product);
			case 'history':
				return this.renderHistory(product);
			case 'price':
				return this.renderPrice(product);
			default:
				return null;
		}
	}

	renderShareButton() {
		const {
			state: { showSharePopover },
			props: { shareLink, onShare },
		} = this;
		const button = (
			<Button
				styles={linkButtonStyles}
				onClick={() => {
					if (!showSharePopover) {
						onShare();
					}
					this.setState({ showSharePopover: !showSharePopover });
				}}
			>
				Share
			</Button>
		);

		return (
			<span ref={(ref) => (this.sharePopoverContainerElement = ref)}>
				<LayoutConstraint reference={button}>
					{showSharePopover ? <ShareLinkPopover link={shareLink} onClose={this.handleCloseSharePopover} /> : null}
				</LayoutConstraint>
			</span>
		);
	}

	renderInfo(product: Product) {
		const { status, prices, currency, isFetchingPrices, emptyValue, emptyPrice, onCurrencyChange } = this.props;

		return (
			<ModalBody>
				{status ?? <Status product={product} />}

				<div className={styles.row}>
					<div className={styles.column}>
						<div className={styles.headRow}>
							<div className={styles.headRowLeft}>
								<Heading level={2}>Product Details</Heading>
							</div>
						</div>

						<Table>
							<tbody>
								<tr>
									<th className={tableStyles.cell}>Country</th>
									<td className={tableStyles.cell}>
										<div className={styles.countryIcon}>
											<CountryIcon
												code={product.dataset.source.country.alpha2}
												name={product.dataset.source.country.name}
											/>
										</div>{' '}
										{`${product.dataset.source.country.name} — `}
										<abbr title={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}>
											{product.dataset.source.shortName}
										</abbr>
									</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Brand name</th>
									<td className={tableStyles.cell}>{product.brandName}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>ATC</th>
									<td className={tableStyles.cell}>{this.renderNullableValue(product.atc)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Active substance</th>
									<td className={tableStyles.cell}>{this.renderNullableValue(product.activeSubstance)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Company</th>
									<td className={tableStyles.cell}>{this.renderNullableValue(product.marketingAuthorizationHolder)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Package</th>
									<td className={tableStyles.cell}>{this.renderNullableValue(product.packageSize)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Dosage form</th>
									<td className={tableStyles.cell}>{this.renderNullableValue(product.dosageForm)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Dosage strength</th>
									<td className={tableStyles.cell}>{this.renderNullableValue(product.dosageStrength)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Valid from - to</th>
									<td className={tableStyles.cell}>
										{product.validFrom ? formatHumanDate(product.validFrom) : emptyValue}
										{' - '}
										{product.validTo ? formatHumanDate(product.validTo) : emptyValue}
									</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Market supply</th>
									<td className={tableStyles.cell}>{this.renderMarketSupply(product.marketSupply)}</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Date of update</th>
									<td className={tableStyles.cell}>
										{product.dataset.productUpdatedAt
											? formatHumanDateTime(product.dataset.productUpdatedAt)
											: emptyValue}
									</td>
								</tr>
								<tr>
									<th className={tableStyles.cell}>Included from</th>
									<td className={tableStyles.cell}>{formatHumanDate(product.includedFrom)}</td>
								</tr>
							</tbody>
						</Table>
					</div>
					<div className={styles.column}>
						<div className={styles.headRow}>
							<div className={styles.headRowLeft}>
								<Heading level={2}>Price Details</Heading>
							</div>
							<div className={styles.headRowRight}>
								<label className={styles.headRowLabel} htmlFor='product-detail-currency-select'>
									Select currency:
								</label>
								<div className={styles.headRowSelect}>
									<CurrencySelect
										currentCurrency={currency}
										id='product-detail-currency-select'
										onChange={onCurrencyChange}
									/>
								</div>
							</div>
						</div>

						<Table>
							<tbody>
								{prices.toArray().map((price) => {
									return (
										<tr key={price.type}>
											<th className={tableStyles.cell}>
												{price.humanFormatType()}
												<ProductPriceInfo
													price={price}
													priceType={price.type}
													product={product}
													styles={priceInfoStyles}
												/>
											</th>
											<td className={styles.priceCell}>
												<FormattedProductPrice
													rounded
													dataSource={product.dataset.source}
													emptyValue={emptyPrice}
													isFetching={Boolean(isFetchingPrices)}
													productPrice={price}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</Table>
					</div>
				</div>
			</ModalBody>
		);
	}

	renderPricesTree(
		product: Product | null,
		prices: List<ProductPrice>,
		isFetchingPrices: boolean
	): {
		[key: string]: React.ReactNode;
	} {
		const { emptyPrice } = this.props;

		const map: { [key: string]: React.ReactNode } = {};
		prices.forEach(
			(price) =>
				(map[price.humanFormatType()] = (
					<FormattedProductPrice
						dataSource={product?.dataset.source || null}
						emptyValue={emptyPrice}
						isFetching={isFetchingPrices}
						productPrice={price}
					/>
				))
		);
		return map;
	}

	renderSource(product: Product) {
		const { emptyValue } = this.props;

		return (
			<ModalBody>
				<Dataset emptyValue={emptyValue} productId={product.id} />
			</ModalBody>
		);
	}

	renderHistory(product: Product) {
		return (
			<ModalBody>
				<ProductHistoryTable productId={product.id} />
			</ModalBody>
		);
	}

	renderPrice(product: Product) {
		const { prices, isFetchingPrices } = this.props;

		return (
			<ModalBody>
				<ProductPriceChart product={product} />

				<Heading level={2}>Current prices (source and calculated)</Heading>
				<div className={styles.row}>
					<TreeBrowser emptyValue='N/A' tree={this.renderPricesTree(product, prices, Boolean(isFetchingPrices))} />
				</div>
			</ModalBody>
		);
	}

	renderMarketSupply(marketSupply: MarketSupply) {
		const { informationPublicSince, statusDateFrom, statusDateTo } = marketSupply;
		return (
			<Fragment>
				{this.renderMarketSupplyStatus(marketSupply)}
				{this.renderDateInterval(statusDateFrom, statusDateTo)}
				{informationPublicSince ? this.renderMarketSupplyPublicSinceTooltip(informationPublicSince) : null}
			</Fragment>
		);
	}

	renderMarketSupplyStatus(marketSupply: MarketSupply) {
		if (marketSupply.isSuspended) {
			return 'Suspended';
		}
		if (marketSupply.isAvailable) {
			return 'Available';
		}
		return this.props.emptyValue;
	}

	renderMarketSupplyPublicSinceTooltip(informationPublicSince: Date) {
		return (
			<Tooltip
				reference={
					<span>
						<Icon name='exfactory-question-mark' />
					</span>
				}
				width={400}
			>
				Information is public since {formatHumanDate(informationPublicSince)}
			</Tooltip>
		);
	}

	renderDateInterval(from: Date | null, to: Date | null): string | null {
		if (from && to) {
			return ` (${formatHumanDate(from)}\xA0-\xA0${formatHumanDate(to)})`;
		}
		if (from) {
			return ` (since ${formatHumanDate(from)})`;
		}
		if (to) {
			return ` (until ${formatHumanDate(to)})`;
		}
		return null;
	}

	renderNullableValue(value: string | null) {
		return value !== null ? value : this.props.emptyValue;
	}

	handleGroupSave = (group: UserProductGroup) => {
		const { onAddProductToGroup, product, filterParameters } = this.props;
		if (product) {
			onAddProductToGroup?.(group, product, filterParameters);
		}
	};

	handleCloseSharePopover = (event: MouseEvent) => {
		if (
			this.sharePopoverContainerElement &&
			event.target !== this.sharePopoverContainerElement &&
			event.target instanceof HTMLElement &&
			!this.sharePopoverContainerElement.contains(event.target)
		) {
			this.setState({ showSharePopover: false });
		}
	};

	render() {
		const { product, products, isFetching, selectedTabName, onClose, onChange, onSelectTab } = this.props;

		if (!product) {
			return (
				<Modal onClose={onClose}>
					<ModalContent>
						<ModalHeader>
							{product === null ? <Heading>Product was not found</Heading> : isFetching ? <ActivityIndicator /> : null}
						</ModalHeader>
					</ModalContent>
				</Modal>
			);
		}

		const productIndex = products.findIndex((otherProduct) => otherProduct.product.id === product.id);

		return (
			<Modal
				onClose={onClose}
				onNext={productIndex < products.length - 1 ? () => onChange(products[productIndex + 1].product.id) : null}
				onPrevious={productIndex > 0 ? () => onChange(products[productIndex - 1].product.id) : null}
			>
				<ModalContent>
					<ModalHeader>
						<Heading>{product.brandName}</Heading>
						<TabBar selectedTabName={selectedTabName} styles={styles} onChange={(tabName) => onSelectTab(tabName)}>
							<Tab name='info'>Product info</Tab>
							<Tab data-testid='source' name='source'>
								Source
							</Tab>
							<Tab name='history'>History log</Tab>
							<Tab name='price'>Price</Tab>
						</TabBar>
					</ModalHeader>

					{this.renderBody(product)}

					<ModalFooter>
						<div className={styles.actions}>
							<p>Add to</p>
							<ProductGroupAddForm productsCount={1} onSave={this.handleGroupSave} />
						</div>
						<div className={styles.info}>
							{this.renderShareButton()}
							<Button styles={secondarySmallButtonStyles} onClick={onClose}>
								Close
							</Button>
						</div>
					</ModalFooter>
				</ModalContent>
			</Modal>
		);
	}
}
